import React, { Fragment, memo, useCallback, useContext, useMemo, useRef } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { createUseStyles, useTheme, Text } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../../lib/Web5Context";
import { SidebarContext } from "./SidebarContext";
import Icon from "../../../shards/Imports/icons/Icon";
import styles from "../styles/MenuItem";
const useStyles = createUseStyles(styles, 'MenuItem');
const MenuItem = /*#__PURE__*/memo(_ref => {
  let {
    className: classNameProp,
    isActive,
    isNewTab,
    onClick,
    hasBadge,
    badgeContent,
    hasTrial,
    icon = '',
    isNew = false,
    link = '',
    navigate = null,
    name = '',
    onMouseEnter = () => {},
    onMouseLeave = () => {}
  } = _ref;
  const menuItemRef = useRef();
  const classes = useStyles();
  const {
    t
  } = useWeb5Context();
  const {
    isExpanded
  } = useContext(SidebarContext);
  const theme = useTheme();
  const rootClassName = classnames(classes.root, {
    [classes.isActive]: isActive,
    [classes.isExpanded]: isExpanded
  }, classNameProp);
  const handleMouseEnter = useCallback(() => {
    const elTop = menuItemRef.current.getBoundingClientRect().top;
    onMouseEnter && onMouseEnter({
      elTop,
      name
    });
  }, [name, onMouseEnter]);
  const handleMouseLeave = useCallback(() => {
    onMouseLeave && onMouseLeave();
  }, [onMouseLeave]);
  const handleOnClick = useCallback(route => e => {
    e.preventDefault();
    const url = `${window.location.origin}${route}`;
    if (e.ctrlKey || e.metaKey || e.button === 1 || e.shiftKey) {
      window.open(url, '_blank');
      return;
    }
    onClick && onClick();
    if (navigate) return navigate(e);
  }, [onClick, navigate]);
  const menuItemChild = useMemo(() => /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(Icon, {
    name: icon,
    className: classes.icon,
    color: isActive ? theme.colors.textLight.primary : theme.colors.textDark.primary
  }), /*#__PURE__*/React.createElement("span", {
    className: classnames(classes.label, {
      [classes.hasBadge]: isNew,
      [classes.hasTrial]: hasTrial
    })
  }, name), isNew && /*#__PURE__*/React.createElement(Text, {
    type: "caption",
    className: classes.newBadge
  }, t('label_sidebar_new')), hasBadge && !!parseInt(badgeContent, 10) && /*#__PURE__*/React.createElement("span", {
    className: classes.notificationBadge
  }), hasTrial && /*#__PURE__*/React.createElement(Icon, {
    name: "trialIcon",
    className: classes.trialIcon,
    color: "currentColor"
  })), [icon, classes.icon, classes.label, classes.hasBadge, classes.hasTrial, classes.newBadge, classes.notificationBadge, classes.trialIcon, isActive, theme, isNew, hasTrial, name, t, hasBadge, badgeContent]);
  const menuItemProps = useMemo(() => {
    let itemProps = {
      href: link,
      className: classnames(rootClassName, {
        [classes.isActive]: isActive
      }),
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      ref: menuItemRef,
      'aria-describedby': 'fmMenuTooltip',
      target: isNewTab ? '_blank' : undefined
    };

    // We need this to use navigate instead of link to be able to allow block navegation
    if (!isNewTab && navigate) {
      itemProps.onClick = handleOnClick(link);
    }
    return itemProps;
  }, [link, rootClassName, classes.isActive, isActive, handleMouseEnter, handleMouseLeave, isNewTab, navigate, handleOnClick]);
  return /*#__PURE__*/React.createElement("a", menuItemProps, menuItemChild);
});
process.env.NODE_ENV !== "production" ? MenuItem.propTypes = {
  className: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  isNew: PropTypes.bool,
  link: PropTypes.string.isRequired,
  navigate: PropTypes.func,
  name: PropTypes.string.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
} : void 0;
export default MenuItem;